/**
 * PDS Design Tokens for SDK
 *
 * Minimal subset of Pantheon Design System tokens optimized for bundle size.
 * These tokens are injected into the Shadow DOM for style isolation.
 *
 * Source: @pantheon-systems/pds-design-tokens
 *
 * Color Mapping from previous implementation:
 * - #3498db (blue) → --pds-color-brand-primary-400 (Pantheon purple)
 * - #3B82F6 (blue) → --pds-color-brand-primary-400 (Pantheon purple)
 * - #e74c3c (red) → --pds-color-status-critical-foreground
 * - #2ecc71 (green) → --pds-color-status-success-foreground
 * - #059669 (green) → --pds-color-status-success-foreground
 * - #D97706 (amber) → --pds-color-status-warning-foreground
 * - #F59E0B (amber) → --pds-color-status-warning-utility
 * - #DC2626 (red) → --pds-color-status-critical-foreground
 */

export const pdsTokensCSS = `
:host {
  /* ===== COLORS ===== */

  /* Brand Primary (Pantheon Purple) */
  --pds-color-brand-primary-100: #f4f3fc;
  --pds-color-brand-primary-200: #e5e0f8;
  --pds-color-brand-primary-300: #cbc2f0;
  --pds-color-brand-primary-400: #4f32ce;
  --pds-color-brand-primary-500: #3017a1;
  --pds-color-brand-primary-600: #12026f;

  /* Neutral Colors */
  --pds-color-neutral-000: #ffffff;
  --pds-color-neutral-100: #f4f4f4;
  --pds-color-neutral-200: #e3e4e5;
  --pds-color-neutral-300: #c3c5c9;
  --pds-color-neutral-400: #6a6e7a;
  --pds-color-neutral-500: #23232d;
  --pds-color-neutral-600: #000000;

  /* Background Colors */
  --pds-color-bg-default: #ffffff;
  --pds-color-bg-default-secondary: #f4f4f4;
  --pds-color-bg-reverse: #23232d;

  /* Foreground/Text Colors */
  --pds-color-fg-default: #23232d;
  --pds-color-fg-default-secondary: #6a6e7a;
  --pds-color-fg-reverse: #ffffff;

  /* Border Colors */
  --pds-color-border-default: #e3e4e5;
  --pds-color-border-input: #c3c5c9;
  --pds-color-border-separator: #c3c5c9;

  /* Status Colors - Critical (Error/Danger) */
  --pds-color-status-critical-background: #ffefee;
  --pds-color-status-critical-foreground: #bc1c11;
  --pds-color-status-critical-utility: #de2e21;

  /* Status Colors - Success */
  --pds-color-status-success-background: #ebfff0;
  --pds-color-status-success-foreground: #217a37;
  --pds-color-status-success-utility: #43c463;

  /* Status Colors - Warning */
  --pds-color-status-warning-background: #ffeed9;
  --pds-color-status-warning-foreground: #b06300;
  --pds-color-status-warning-utility: #ffa126;

  /* Status Colors - Info */
  --pds-color-status-info-background: #d7edff;
  --pds-color-status-info-foreground: #0875e1;
  --pds-color-status-info-utility: #a6d2ff;

  /* Interactive Colors */
  --pds-color-interactive-background-active: #e3e4e5;
  --pds-color-interactive-background-hover: #f4f4f4;
  --pds-color-interactive-focus: #0f62fe;
  --pds-color-interactive-link-default: #0f62fe;
  --pds-color-interactive-link-hover: #4f32ce;
  --pds-color-interactive-link-active: #3017a1;

  /* Button Colors - Primary */
  --pds-color-button-primary-background-default: #4f32ce;
  --pds-color-button-primary-background-hover: #3017a1;
  --pds-color-button-primary-background-active: #12026f;
  --pds-color-button-primary-foreground-default: #ffffff;

  /* Button Colors - Secondary */
  --pds-color-button-secondary-background-default: rgba(0, 0, 0, 0);
  --pds-color-button-secondary-background-hover: #f4f3fc;
  --pds-color-button-secondary-border-default: #4f32ce;
  --pds-color-button-secondary-foreground-default: #4f32ce;

  /* Button Colors - Critical (Danger) */
  --pds-color-button-critical-background-default: #bc1c11;
  --pds-color-button-critical-background-hover: #9e140b;
  --pds-color-button-critical-foreground-default: #ffffff;

  /* Elevation/Shadows */
  --pds-elevation-overlay: 0px 0px 0px 1px rgba(0, 0, 0, 0.08), 0px 1px 1px 0px rgba(0, 0, 0, 0.02), 0px 8px 16px -4px rgba(0, 0, 0, 0.04), 0px 24px 32px -8px rgba(0, 0, 0, 0.06);
  --pds-elevation-raised: 0px 0px 0px 1px rgba(0, 0, 0, 0.08), 0px 2px 2px 0px rgba(0, 0, 0, 0.04), 0px 8px 8px -8px rgba(0, 0, 0, 0.04);

  /* ===== SPACING ===== */
  --pds-spacing-5xs: 0.25rem;
  --pds-spacing-4xs: 0.328rem;
  --pds-spacing-3xs: 0.41rem;
  --pds-spacing-2xs: 0.512rem;
  --pds-spacing-xs: 0.64rem;
  --pds-spacing-s: 0.8rem;
  --pds-spacing-m: 1rem;
  --pds-spacing-l: 1.25rem;
  --pds-spacing-xl: 1.563rem;
  --pds-spacing-2xl: 1.953rem;
  --pds-spacing-3xl: 2.441rem;
  --pds-spacing-4xl: 3.052rem;

  /* ===== TYPOGRAPHY ===== */
  --pds-typography-ff-default: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  --pds-typography-ff-mono: 'Source Code Pro', monospace;

  --pds-typography-fw-regular: 400;
  --pds-typography-fw-medium: 500;
  --pds-typography-fw-semibold: 600;
  --pds-typography-fw-bold: 700;

  --pds-typography-size-xs: 0.694rem;
  --pds-typography-size-s: 0.833rem;
  --pds-typography-size-m: 1rem;
  --pds-typography-size-l: 1.2rem;
  --pds-typography-size-xl: 1.44rem;

  --pds-typography-lh-s: 120%;
  --pds-typography-lh-m: 140%;
  --pds-typography-lh-l: 165%;

  /* ===== BORDERS ===== */
  --pds-border-radius-default: 0.1875rem;
  --pds-border-radius-button: 0.1875rem;
  --pds-border-radius-input: 0.25rem;
  --pds-border-radius-container: 0.375rem;
  --pds-border-width-default: 1px;

  /* ===== ANIMATION ===== */
  --pds-animation-transition-default-duration: 200ms;
  --pds-animation-transition-default-timing-function: cubic-bezier(.2, 0, 0, 1);

  /* ===== Z-INDEX ===== */
  --pds-z-index-dropdown: 300;
  --pds-z-index-overlay: 700;
  --pds-z-index-modal: 900;
  --pds-z-index-max: 9999;
}

/* ===== ANIMATIONS ===== */
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Screen reader only utility class */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}
`;

/**
 * Semantic token aliases for easier component usage
 * Maps semantic meanings to PDS token variable references
 */
export const tokens = {
  // Colors
  colors: {
    // Primary brand color
    primary: 'var(--pds-color-brand-primary-400)',
    primaryHover: 'var(--pds-color-brand-primary-500)',
    primaryActive: 'var(--pds-color-brand-primary-600)',
    primaryLight: 'var(--pds-color-brand-primary-100)',
    primaryBorder: 'var(--pds-color-brand-primary-200)',

    // Backgrounds
    bgDefault: 'var(--pds-color-bg-default)',
    bgSecondary: 'var(--pds-color-bg-default-secondary)',
    bgReverse: 'var(--pds-color-bg-reverse)',

    // Text/Foreground
    textDefault: 'var(--pds-color-fg-default)',
    textSecondary: 'var(--pds-color-fg-default-secondary)',
    textReverse: 'var(--pds-color-fg-reverse)',

    // Borders
    borderDefault: 'var(--pds-color-border-default)',
    borderInput: 'var(--pds-color-border-input)',

    // Status - Critical/Error
    criticalBg: 'var(--pds-color-status-critical-background)',
    criticalFg: 'var(--pds-color-status-critical-foreground)',
    critical: 'var(--pds-color-status-critical-utility)',

    // Status - Success
    successBg: 'var(--pds-color-status-success-background)',
    successFg: 'var(--pds-color-status-success-foreground)',
    success: 'var(--pds-color-status-success-utility)',

    // Status - Warning
    warningBg: 'var(--pds-color-status-warning-background)',
    warningFg: 'var(--pds-color-status-warning-foreground)',
    warning: 'var(--pds-color-status-warning-utility)',

    // Status - Info
    infoBg: 'var(--pds-color-status-info-background)',
    infoFg: 'var(--pds-color-status-info-foreground)',
    info: 'var(--pds-color-status-info-utility)',

    // Interactive
    interactiveHover: 'var(--pds-color-interactive-background-hover)',
    interactiveActive: 'var(--pds-color-interactive-background-active)',
    focus: 'var(--pds-color-interactive-focus)',
    link: 'var(--pds-color-interactive-link-default)',

    // Button
    buttonPrimaryBg: 'var(--pds-color-button-primary-background-default)',
    buttonPrimaryBgHover: 'var(--pds-color-button-primary-background-hover)',
    buttonPrimaryFg: 'var(--pds-color-button-primary-foreground-default)',
    buttonCriticalBg: 'var(--pds-color-button-critical-background-default)',
    buttonCriticalFg: 'var(--pds-color-button-critical-foreground-default)',

    // Neutrals
    white: 'var(--pds-color-neutral-000)',
    gray100: 'var(--pds-color-neutral-100)',
    gray200: 'var(--pds-color-neutral-200)',
    gray300: 'var(--pds-color-neutral-300)',
    gray400: 'var(--pds-color-neutral-400)',
    gray500: 'var(--pds-color-neutral-500)',
    gray800: '#1e293b', // Slate-800 for dark tooltips (no PDS equivalent)
    black: 'var(--pds-color-neutral-600)',

    // Primary variants for borders
    primaryDark: 'var(--pds-color-brand-primary-500)',
  },

  // Spacing
  spacing: {
    '5xs': 'var(--pds-spacing-5xs)',
    '4xs': 'var(--pds-spacing-4xs)',
    '3xs': 'var(--pds-spacing-3xs)',
    '2xs': 'var(--pds-spacing-2xs)',
    xs: 'var(--pds-spacing-xs)',
    s: 'var(--pds-spacing-s)',
    m: 'var(--pds-spacing-m)',
    l: 'var(--pds-spacing-l)',
    xl: 'var(--pds-spacing-xl)',
    '2xl': 'var(--pds-spacing-2xl)',
    '3xl': 'var(--pds-spacing-3xl)',
    '4xl': 'var(--pds-spacing-4xl)',
  },

  // Typography
  typography: {
    fontFamily: 'var(--pds-typography-ff-default)',
    fontFamilyMono: 'var(--pds-typography-ff-mono)',
    fontWeight: {
      regular: 'var(--pds-typography-fw-regular)',
      medium: 'var(--pds-typography-fw-medium)',
      semibold: 'var(--pds-typography-fw-semibold)',
      bold: 'var(--pds-typography-fw-bold)',
    },
    fontSize: {
      xs: 'var(--pds-typography-size-xs)',
      s: 'var(--pds-typography-size-s)',
      m: 'var(--pds-typography-size-m)',
      l: 'var(--pds-typography-size-l)',
      xl: 'var(--pds-typography-size-xl)',
    },
    lineHeight: {
      s: 'var(--pds-typography-lh-s)',
      m: 'var(--pds-typography-lh-m)',
      l: 'var(--pds-typography-lh-l)',
    },
  },

  // Borders
  borders: {
    radiusDefault: 'var(--pds-border-radius-default)',
    radiusButton: 'var(--pds-border-radius-button)',
    radiusInput: 'var(--pds-border-radius-input)',
    radiusContainer: 'var(--pds-border-radius-container)',
    widthDefault: 'var(--pds-border-width-default)',
  },

  // Shadows/Elevation
  elevation: {
    overlay: 'var(--pds-elevation-overlay)',
    raised: 'var(--pds-elevation-raised)',
  },

  // Animation
  animation: {
    duration: 'var(--pds-animation-transition-default-duration)',
    /** Duration in milliseconds for use in JavaScript (matches CSS variable) */
    durationMs: 200,
    timing: 'var(--pds-animation-transition-default-timing-function)',
    /** Raw timing function for use outside Shadow DOM (matches CSS variable) */
    timingRaw: 'cubic-bezier(0.2, 0, 0, 1)',
  },

  // Z-Index
  zIndex: {
    dropdown: 'var(--pds-z-index-dropdown)',
    overlay: 'var(--pds-z-index-overlay)',
    modal: 'var(--pds-z-index-modal)',
    max: 'var(--pds-z-index-max)',
  },

  // Layout
  layout: {
    sidebarWidth: '360px',
  },
} as const;
